import React, { useEffect, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { Checkbox, FormGroup, makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { runInAction, toJS } from 'mobx';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckboxDefaultIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import RadioButtonDefaultIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import { OptionSelector } from './OptionSelector';

const useStyles = makeStyles(theme => ({
  root: {
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    marginLeft: 0,
    marginRight: 0,
  },
  label: {
    '&.Mui-disabled': {
      color: 'inherit !important',
    },
    color: 'inherit',
    fontWeight: 'inherit',
    fontSize: 'inherit',
    fontFamily: 'inherit',
  },
  checkbox: {
    '&:hover': {
      backgroundColor: 'transparent', // Remove hover background color
    },
    '&.Mui-checked:hover': {
      backgroundColor: 'transparent', // Remove hover background color when checked
    },
  },
}));

export const Option = observer(props => {
  const classes = useStyles();
  const { settings, environment, updateOrder, activePersonNo, validity, question, contentLayoutModuleId, checkConditions, checkConditionsOld, validate } =
    props;
  const orders = toJS(props.orders);
  const { prefixClassName, displayType, hidden, readonly, required, disableChangeLock } = settings;

  const [initValidation, setInitValidation] = useState(true);
  const [runValidation, setRunValidation] = useState(false);

  const options = Array.from(question.questionOption);
  const answer =
    (orders[activePersonNo] && orders[activePersonNo].answer && orders[activePersonNo]?.answer?.find(a => a.questionId === question.questionId)) || {};

  useEffect(() => {
    if (orders && orders[activePersonNo]?.badgeNo && initValidation) {
      setInitValidation(false);
      validateOptions();
    }
  }, [orders, activePersonNo, initValidation]);

  useEffect(() => {
    if (contentLayoutModuleId) {
      setRunValidation(true);
      checkConditionsOld(contentLayoutModuleId);
      checkConditions(`QUESTIONOPTION.${question.questionId}`);
    }
  }, [activePersonNo]);

  useEffect(() => {
    if (answer?.answerRow) {
      validateOptions();
    }
  }, [answer]);

  useEffect(() => {
    if (runValidation) {
      setRunValidation(false);
      validateOptions();
    }
  }, [runValidation]);

  const handleChange = (value, optionId, options = null) => {
    let order = _.cloneDeep(orders[activePersonNo]);

    if (!order?.answer && order) {
      order.answer = [];
    }

    let answer = order.answer.find(a => a.questionId === question.questionId);

    if (!answer) {
      answer = {
        answerId: 0,
        interactionKey: null,
        orderId: 0,
        questionId: question.questionId,
        answerRow: [],
      };
      order.answer.push(answer);
    }

    if (!answer.answerRow) {
      answer.answerRow = [];
    }

    //I do not dare to touch this part of the code so created a new case for multislected options
    if (options && Array.isArray(options)) {

      if (options.length > question.requiredMax) {
        return;
      }

      answer.answerRow = options.map(o => ({
        answerId: answer.answerRow.find(ar => ar.questionOptionId === o.value)?.answerId || 0,
        answerRowId: answer.answerRow.find(ar => ar.questionOptionId === o.value)?.answerRowId || 0,
        questionOptionId: o.value,
        text: null,
        deleted: false,
      }));
 
      order.answer[order.answer.indexOf(order.answer.find(a => a.questionId === question.questionId))] = { ...answer };


    } else {
      if (value === true && question.requiredMax === 1) {
        answer.answerRow.map(ar => {
          ar.deleted = true;
        });
      }
      let row = answer.answerRow.find(ar => ar.questionOptionId === optionId);
      if (row) {
        row.deleted = !value;
      } else {
        answer.answerRow.push({
          answerId: answer.answerId,
          answerRowId: 0,
          questionOptionId: optionId,
          text: null,
          deleted: false,
        });
      }
      answer.answerRow = answer.answerRow.filter(ar => {
        return !ar.deleted;
      });
      order.answer[order.answer.indexOf(order.answer.find(a => a.questionId === question.questionId))] = { ...answer };
    }

    updateOrder(order);
    setRunValidation(true);
    checkConditionsOld(contentLayoutModuleId);
    checkConditions(`QUESTIONOPTION.${question.questionId}`);
  };

  const getCheckedCount = () => {
    if (orders.length > 0) {
      let order = orders[activePersonNo];
      if (order.answer) {
        let answer = order.answer.find(a => a.questionId === question.questionId);
        if (answer && answer.answerRow && answer.answerRow.length > 0) {
          let rows = answer.answerRow.filter(ar => ar.questionOptionId != 0 && !ar.deleted);
          return rows.length;
        }
      }
    }
    return 0;
  };

  const validateOptions = useCallback(() => {
    let isValid = true;
    let reason = '';
    let { requiredMax, requiredMin } = question;

    const checkedCount = getCheckedCount();

    if (readonly || hidden) {
      isValid = true;
      return isValid;
    } else if (required && checkedCount === 0) {
      isValid = false;
      reason = 'required';
    } else if (requiredMin > 0 && checkedCount > 0 && checkedCount < requiredMin) {
      isValid = false;
      reason = 'minLengthOption';
    } else if (requiredMax > 0 && checkedCount > 0 && checkedCount > requiredMax) {
      isValid = false;
      reason = 'maxLengthOption';
    }

    if (validity) {
      runInAction(() => {
        validity.valid = isValid;
        validity.reason = reason;
      });
    }
  }, [validity, question, getCheckedCount, readonly, hidden, required]);

  const isChangeLocked = useMemo(() => {
    const hasOrder = orders?.[activePersonNo]?.badgeNo;
    return hasOrder && disableChangeLock === false;
  }, [disableChangeLock, orders, activePersonNo]);

  const isValid = useMemo(() => {
    const checkedCount = getCheckedCount();
    if (isChangeLocked) return true;
    if (checkedCount.length > 0 && validity?.valid === false) return false;
    else if (checkedCount.length > 0 && validity?.valid === true) return true;
    else if (validate && validity?.valid === false) return false;
    return undefined;
  }, [validity, validate, getCheckedCount, isChangeLocked]);

  if (displayType?.toLowerCase() === 'select') {
    return <OptionSelector {...{ ...props, ...settings, question, handleChange, disabled: isChangeLocked || readonly, valid: isValid, options }} />;
  }

  if (question.requiredMin === 1 && question.requiredMax === 1) {
    return (
      <RadioGroup
        className={`${prefixClassName} checkbox-group`}
        onChange={e => {
          runInAction(() => {
            if (props?.userInteracted === false) {
              // props.userInteracted = true;
            }
          });
          handleChange(true, parseInt(e.target.value));
        }}
      >
        {options
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(qo => (
            <FormControlLabel
              className="option"
              classes={classes}
              disabled={readonly || isChangeLocked}
              checked={Boolean(answer?.answerRow?.find(ar => ar.questionOptionId === qo.questionOptionId))}
              key={qo.questionOptionId}
              control={
                <Radio
                  classes={{ root: classes.root }}
                  disableRipple
                  className={classes.checkbox}
                  style={{ opacity: readonly || isChangeLocked ? 0.3 : 1, paddingLeft: 0 }}
                  icon={
                    displayType?.toLowerCase() === 'radio' ? (
                      <RadioButtonDefaultIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
                    ) : (
                      <CheckboxDefaultIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
                    )
                  }
                  checkedIcon={
                    displayType?.toLowerCase() === 'radio' ? (
                      <RadioButtonCheckedIcon className="checkbox cms-checkbox" />
                    ) : (
                      <CheckBoxIcon className="checkbox cms-checkbox" />
                    )
                  }
                />
              }
              value={qo.questionOptionId.toString()}
              label={qo?.questionOptionLanguage?.find(fl => fl.languageCode === environment.lang)?.label}
            />
          ))}
      </RadioGroup>
    );
  }

  return (
    <FormGroup className={`${prefixClassName} checkbox-group`}>
      {options
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(qo => (
          <FormControlLabel
            className="option"
            classes={classes}
            key={qo.questionOptionId}
            disabled={readonly || isChangeLocked}
            onChange={e => {
              handleChange(e.target.checked, qo.questionOptionId);
              runInAction(() => {});
            }}
            checked={Boolean(answer?.answerRow?.find(ar => ar.questionOptionId === qo.questionOptionId))}
            control={
              <Checkbox
                classes={{ root: classes.root }}
                disableRipple
                style={{ opacity: readonly || isChangeLocked ? 0.3 : 1, paddingLeft: 0 }}
                className={classes.checkbox}
                icon={
                  displayType?.toLowerCase() === 'radio' ? (
                    <RadioButtonDefaultIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
                  ) : (
                    <CheckboxDefaultIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
                  )
                }
                checkedIcon={
                  displayType?.toLowerCase() === 'radio' ? (
                    <RadioButtonCheckedIcon className="checkbox cms-checkbox" />
                  ) : (
                    <CheckBoxIcon className="checkbox cms-checkbox" />
                  )
                }
              />
            }
            label={<p>{qo?.questionOptionLanguage?.find(fl => fl.languageCode === environment.lang)?.label || '-'}</p>}
          />
        ))}
    </FormGroup>
  );
});
